import React from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import IconButton from '@material-ui/core/IconButton';
import { CheckIcon, CloseIcon, ErrorIcon, InfoDot } from 'sharedComponents/Icons';
import Slide from '@material-ui/core/Slide';
import { Button, Icon } from '@material-ui/core';
import cx from 'classnames';

const AUTOHIDE_DURATION = 5000;

const typeIcon = {
  success: CheckIcon,
  warning: ErrorIcon,
  error: ErrorIcon,
  info: InfoDot,
  CheckIcon,
};

const position = {
  topCenter: {
    vertical: 'top',
    horizontal: 'center',
  },
  bottomCenter: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  rightTop: {
    vertical: 'top',
    horizontal: 'right',
  },
};

/* When notification closing the animation always using default direction since retaining last notification direction to match closing nofication animation */
let currentDirection = 'bottomCenter';

const Notification = ({ classes, notification, clearNotification, isPermanent }) => {
  const {
    message,
    type,
    id,
    duration,
    description,
    direction = currentDirection,
    ClickAwayListenerProps = {},
    secondaryButton,
    iconType,
    rootStyle = {},
    snackBarContentStyle,
    onClose,
  } = notification;

  const handleClose = async () => {
    await clearNotification(id);
    currentDirection = direction;

    if (onClose) {
      onClose();
    }
  };

  const handleSecBtnClick = () => {
    secondaryButton.onClick();
    // Making sure notification closed after secondary button clicked
    handleClose();
  };

  currentDirection = direction;

  const NotificationIcon = () => (
    <Icon
      component={typeIcon[iconType || type]}
      className={cx(classes.icon, snackBarContentStyle && classes.redIcon)}
    />
  );

  const defineRootStyle = () => {
    if (snackBarContentStyle) return `${classes.snackBarContentStyle}`;
    return `${classes.root} ${classes[type]} `;
  };

  return (
    <Snackbar
      open={!!message}
      onClose={handleClose}
      ContentProps={{
        'aria-describedby': 'message-id',
      }}
      TransitionComponent={Slide}
      ClickAwayListenerProps={ClickAwayListenerProps}
      anchorOrigin={position[direction]}
      autoHideDuration={duration || AUTOHIDE_DURATION}
      classes={{
        anchorOriginTopCenter: classes.anchorOriginTopCenter,
        anchorOriginBottomCenter: classes.anchorOriginBottomCenter,
      }}
      style={rootStyle}
    >
      <SnackbarContent
        classes={{
          root: defineRootStyle(),
          message: classes.snackbarMessage,
          action: classes.snackbarAction,
        }}
        aria-describedby="client-snackbar"
        message={
          <div>
            <span id="client-snackbar" className={classes.message}>
              {!!NotificationIcon && NotificationIcon()}
              <span className={classes.messageText}>{message}</span>
            </span>
            {description && <div className={classes.description}>{description}</div>}
          </div>
        }
        action={
          <>
            {secondaryButton && (
              <Button color="inherit" size="small" onClick={handleSecBtnClick} data-testid="action-secondary-btn">
                {secondaryButton.buttonText}
              </Button>
            )}
            {!isPermanent && (
              <IconButton
                key="close"
                aria-label="close"
                color="inherit"
                classes={{ root: classes.closeIcon }}
                onClick={handleClose}
                data-testid="action-close-btn"
              >
                <Icon component={CloseIcon} className={cx(classes.icon, snackBarContentStyle && classes.redIcon)} />
              </IconButton>
            )}
          </>
        }
      />
    </Snackbar>
  );
};

Notification.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  notification: PropTypes.shape({
    message: PropTypes.string,
    type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
    direction: PropTypes.oneOf(['rightTop', 'bottomCenter']),
    duration: PropTypes.number,
    description: PropTypes.string,
    secondaryButton: PropTypes.shape({
      buttonText: PropTypes.string,
      onClick: PropTypes.func,
    }),
    iconType: PropTypes.string,
    onClose: PropTypes.func,
  }),
  /* Callback to clear notification state */
  clearNotification: PropTypes.func,
  isPermanent: PropTypes.bool,
};

Notification.defaultProps = {
  classes: {},
  isPermanent: false,
};

export default Notification;
